import { all, call, takeLeading, put, select, takeEvery } from '@redux-saga/core/effects';
import axios from 'axios';

import { ERROR, MARKET_BUZZ_PAGE_SIZE, SUCCESS } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';
import { retrieveFromLocalStorage } from '../../utils/util';

import {
  requestCreatePost,
  requestViewPosts,
  requestUpdatePost,
  requestDeletePost,
  requestPopulateMarketBuzzFilter,
  requestCreateReaction,
  requestViewReactions,
  requestRetrieveChat,
  requestCreateComment,
  requestViewComments,
  requestUpdateComment,
} from './market-buzz.actions';
import {
  getCreatePostRequest,
  getViewPostsRequest,
  getUpdatePostRequest,
  getDeletePostRequest,
  getPopulateMarketBuzzFilterRequest,
  getCreateReactionRequest,
  getViewReactionsRequest,
  getRetrieveChatRequest,
  getCreateCommentRequest,
  getViewCommentsRequest,
  getUpdateCommentRequest,
} from './market-buzz.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import {
  setPosts,
  amendPosts,
  deletePost,
  setOrganisations,
  setPersons,
  setReactions,
  setFilters,
  setPage,
  addCommentsToPost,
  addNewCommentToPost,
  addCommentsToComment,
  addNewCommentToComment,
  deleteComment,
  addPost,
  replacePost,
  replaceComment,
} from '../../slices/market-buzz/market-buzz.slice';

export function* performCreatePostRequest({ payload }) {
  const { post, onSuccess } = payload;

  try {
    yield put(addSpinner('Creating Post'));

    const personExternalReference = yield call(retrieveFromLocalStorage, 'personExternalReference');

    const [endpoint, options] = getCreatePostRequest({ ...post, personExternalReference });

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      onSuccess();
    }

    yield put(addPost(data.post));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Creating Post'));
  }
}

export function* watchForCreatePostRequest() {
  yield takeLeading(requestCreatePost.type, performCreatePostRequest);
}

export function* performViewPostsRequest({ payload }) {
  const { filters, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving Posts'));

    const { page } = yield select(({ marketBuzz }) => marketBuzz);

    const [endpoint, options] = getViewPostsRequest({
      from: filters.datePostedFrom,
      to: filters.datePostedTo,
      personExternalReference: filters.user?.personExternalReference,
      organisationExternalReference: filters.organisation?.organizationExternalReference,
      size: MARKET_BUZZ_PAGE_SIZE,
      page,
    });

    const { data } = yield call(axios, endpoint, options);
    const { posts } = data;

    yield put(setFilters(filters));

    if (page === 0) {
      yield put(setPosts(posts));
      yield put(setPage(1));
    } else {
      const numberOfPostsInStore = yield select(({ marketBuzz }) => marketBuzz.posts.length);
      const numberOfPostsInPage = numberOfPostsInStore % MARKET_BUZZ_PAGE_SIZE;

      if (numberOfPostsInPage !== 0) {
        posts.splice(0, numberOfPostsInPage);
      } else {
        yield put(setPage(page + 1));
      }

      yield put(amendPosts(posts));
    }

    if (onSuccess) {
      onSuccess(posts.length);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Posts'));
  }
}

export function* watchForViewPostsRequest() {
  yield takeLeading(requestViewPosts.type, performViewPostsRequest);
}

export function* performUpdatePostRequest({ payload }) {
  const { post, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Post'));

    const [endpoint, options] = getUpdatePostRequest(post);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      onSuccess();
    }

    yield put(replacePost(data.post));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Post'));
  }
}

export function* watchForUpdatePostRequest() {
  yield takeLeading(requestUpdatePost.type, performUpdatePostRequest);
}

export function* performDeletePostRequest({ payload }) {
  const {
    postExternalReference,
    parentPostExternalReference,
    parentCommentExternalReference,
    reaction,
    onSuccess,
    onError,
  } = payload;

  try {
    if (!reaction) {
      yield put(addSpinner('Deleting Post'));
    }

    const [endpoint, options] = getDeletePostRequest(postExternalReference);

    yield call(axios, endpoint, options);

    if (parentPostExternalReference) {
      yield put(
        deleteComment({
          postExternalReference: parentPostExternalReference,
          parentExternalReference: parentCommentExternalReference,
          commentExternalReference: postExternalReference,
        })
      );
    } else {
      yield put(deletePost(postExternalReference));
    }

    if (onSuccess) {
      onSuccess();
    }

    if (reaction) {
      yield put(requestViewReactions());
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));

    if (onError) {
      onError();
    }
  } finally {
    yield put(removeSpinner('Deleting Post'));
  }
}

export function* watchForDeletePostRequest() {
  yield takeLeading(requestDeletePost.type, performDeletePostRequest);
}

export function* performPopulateMarketBuzzFilterRequest() {
  try {
    yield put(addSpinner('Retrieving Organisations & Persons'));

    const [endpoint, options] = getPopulateMarketBuzzFilterRequest();

    const { data } = yield call(axios, endpoint, options);

    yield put(setOrganisations(data.organizations));
    yield put(setPersons(data.persons));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Organisations & Persons'));
  }
}

export function* watchForPopulateMarketBuzzFilterRequest() {
  yield takeLeading(requestPopulateMarketBuzzFilter.type, performPopulateMarketBuzzFilterRequest);
}

export function* performCreateReactionRequest({ payload }) {
  const { reaction, onSuccess, onError } = payload;

  try {
    const personExternalReference = yield call(retrieveFromLocalStorage, 'personExternalReference');

    const [endpoint, options] = getCreateReactionRequest({ ...reaction, personExternalReference });

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      onSuccess(data);
    }

    yield put(requestViewReactions());
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));

    if (onError) {
      onError();
    }
  }
}

export function* watchForCreateReactionRequest() {
  yield takeLeading(requestCreateReaction.type, performCreateReactionRequest);
}

export function* performViewReactionsRequest() {
  try {
    const personExternalReference = yield call(retrieveFromLocalStorage, 'personExternalReference');

    const [endpoint, options] = getViewReactionsRequest(personExternalReference);

    const { data } = yield call(axios, endpoint, options);

    yield put(setReactions(data.reactions));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewReactionsRequest() {
  yield takeLeading(requestViewReactions.type, performViewReactionsRequest);
}

export function* performRetrieveChatRequest({ payload }) {
  const { postCreatorExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving Chat'));

    const personExternalReference = yield call(retrieveFromLocalStorage, 'personExternalReference');

    const [endpoint, options] = getRetrieveChatRequest(personExternalReference, postCreatorExternalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      onSuccess(data.chatExternalReference);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Chat'));
  }
}

export function* watchForRetrieveChatRequest() {
  yield takeLeading(requestRetrieveChat.type, performRetrieveChatRequest);
}

export function* performCreateCommentRequest({ payload }) {
  const { comment, postExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Creating Comment'));

    const personExternalReference = yield call(retrieveFromLocalStorage, 'personExternalReference');

    const [endpoint, options] = getCreateCommentRequest({ ...comment, personExternalReference });

    const { data } = yield call(axios, endpoint, options);

    if (!postExternalReference) {
      yield put(
        addNewCommentToPost({
          postExternalReference: comment.parentPostExternalReference,
          comment: data.comments[0],
        })
      );
    } else {
      yield put(
        addNewCommentToComment({
          commentExternalReference: comment.parentPostExternalReference,
          comment: data.comments[0],
          postExternalReference,
        })
      );
    }

    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Creating Comment'));
  }
}

export function* watchForCreateCommentRequest() {
  yield takeLeading(requestCreateComment.type, performCreateCommentRequest);
}

export function* performViewCommentsRequest({ payload }) {
  const { postExternalReference, page, size, parentPostExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving Comments'));

    const [endpoint, options] = getViewCommentsRequest(postExternalReference, page, size);

    const { data } = yield call(axios, endpoint, options);

    if (parentPostExternalReference) {
      yield put(
        addCommentsToComment({
          parentPostExternalReference,
          commentExternalReference: postExternalReference,
          comments: data.comments,
        })
      );
    } else {
      yield put(addCommentsToPost({ postExternalReference, comments: data.comments, page }));
    }

    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Comments'));
  }
}

export function* watchForViewCommentsRequest() {
  yield takeEvery(requestViewComments.type, performViewCommentsRequest);
}

export function* performUpdateCommentRequest({ payload }) {
  const { comment, postExternalReference, parentExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Comment'));

    const [endpoint, options] = getUpdateCommentRequest(comment);

    const { data } = yield call(axios, endpoint, options);

    yield put(replaceComment({ postExternalReference, parentExternalReference, comment: data.comments[0] }));

    if (onSuccess) {
      onSuccess();
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Comment'));
  }
}

export function* watchForUpdateCommentRequest() {
  yield takeLeading(requestUpdateComment.type, performUpdateCommentRequest);
}

export default function* marketBuzzSaga() {
  yield all([
    watchForCreatePostRequest(),
    watchForViewPostsRequest(),
    watchForUpdatePostRequest(),
    watchForDeletePostRequest(),
    watchForPopulateMarketBuzzFilterRequest(),
    watchForCreateReactionRequest(),
    watchForViewReactionsRequest(),
    watchForRetrieveChatRequest(),
    watchForCreateCommentRequest(),
    watchForViewCommentsRequest(),
    watchForUpdateCommentRequest(),
  ]);
}
